import { z } from 'zod';
import { NotificationTypes } from './NotificationTypes.enum';
import { NotificationChannelTypes } from './NotificationChannelTypes.enum';

// ============================================================
// Schemas (source of truth)
// ============================================================

// Per-notification-type setting for a user
export const userNotificationSettingSchema = z.object({
	frequency: z.string().optional(),
	channels: z
		.object(
			Object.fromEntries(Object.values(NotificationChannelTypes).map((key) => [key, z.boolean()])) as {
				[K in NotificationChannelTypes]: z.ZodBoolean;
			}
		)
		.partial()
		.optional(),
});

// Per-notification-type setting for base (project-level) — extends user with recipients
export const baseNotificationSettingSchema = userNotificationSettingSchema.extend({
	recipients: z.array(z.string()),
});

// User notification settings — per-user, per-project
export const userNotificationSettingsSchema = z.object({
	_id: z.string().optional(),
	username: z.string(),
	projectId: z.string(),
	settings: z.record(z.string(), userNotificationSettingSchema.optional()),
});

// Base notification settings — project-level defaults
export const baseNotificationSettingsSchema = z.object({
	_id: z.string().optional(),
	projectId: z.string(),
	isBase: z.literal(true),
	settings: z.record(z.string(), baseNotificationSettingSchema.optional()),
});

// Union schema for repository use (backwards-compatible)
export const notificationSettingsSchema = z.object({
	_id: z.string().optional(),
	projectId: z.string().nullable(),
	isBase: z.boolean().optional(),
	username: z.string().optional(),
	settings: z
		.record(
			z.string(),
			userNotificationSettingSchema.extend({ recipients: z.array(z.string()).optional() }).optional()
		)
		.optional(),
	recipients: z.array(z.string()).optional(),
});

// ============================================================
// Types (derived from schemas)
// ============================================================

export type IUserNotificationSetting = z.infer<typeof userNotificationSettingSchema>;
export type IBaseNotificationSetting = z.infer<typeof baseNotificationSettingSchema>;
export type IUserNotificationSettings = z.infer<typeof userNotificationSettingsSchema>;
export type IBaseNotificationSettings = z.infer<typeof baseNotificationSettingsSchema>;
export type INotificationSettings = IUserNotificationSettings | IBaseNotificationSettings;
